import { getActivityTypeName } from 'Components/utils/booking/bookingUtils';
import {
  getReservedServicesByAttendee,
  updatePriceInfo,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import { isEmpty, omit, groupBy, values } from 'lodash';

const formatCartBookings = (
  cartBookings: Array<Cart>,
  bookings: Array<BookingEntity>,
): Array<Cart> => {
  return cartBookings.map((cartBooking) => {
    const booking = bookings.find(({ id }) => id === cartBooking.bookingId);
    return {
      ...cartBooking,
      ...(booking && {
        booking,
      }),
    };
  });
};

const formatReservedServicesByAttendee = (
  cartBookings: Array<Cart>,
  appContext: AppContext,
): any => {
  return cartBookings.reduce((acc, cartBooking) => {
    acc[cartBooking?.bookingId] = (cartBooking?.stayServices || []).reduce(
      (acc2, { activities, rentals, stayIndex }) => {
        const { booking } = cartBooking;
        const stay = (booking?.stays[stayIndex] || {}) as StayEntity;
        const availableActivitiesUpdatedWithCart: ReadonlyArray<UpsellActivity> =
          stay.upsells &&
          [...(stay.upsells.activities || [])].map((activity) => ({
            ...activity,
            pricingInfo: updatePriceInfo(activity.pricingInfo, activities || [], appContext.locale),
          }));
        const availableRentalsUpdatedWithCart: ReadonlyArray<UpsellRental> =
          stay?.upsells &&
          [...(stay.upsells.rentals || [])].map((rental) => ({
            ...rental,
            pricingInfo: updatePriceInfo(rental.pricingInfo, rentals || [], appContext.locale),
          }));
        acc2[stayIndex] = getReservedServicesByAttendee({
          attendees: cartBooking?.attendees || [],
          availableActivities: availableActivitiesUpdatedWithCart,
          availableRentals: availableRentalsUpdatedWithCart,
          locale: appContext.locale,
          paymentStatus: 'isRented',
        });
        return acc2;
      },
      {},
    );
    return acc;
  }, {});
};

const formatCartTrackingData = (cartBookings: Array<Cart>): any => {
  return cartBookings
    .filter((bookingCart) => !bookingCart.isEmpty)
    .flatMap((bookingCart) => {
      return (bookingCart?.stayServices || []).map((cart) => {
        const pickYourRoomUpsell =
          bookingCart.price &&
          bookingCart.price.detail.find(({ service }) => service === 'ROOM_SELECTION_SERVICE');
        let trackingData: Array<Record<string, unknown>> = Object.values(
          omit(cart, ['stayIndex', 'pickRoom']),
        )
          .flat()
          .map((service) => {
            type CartTrackingService = {
              type?: string;
              serviceInfo?: { subtype?: string };
              productId?: string;
              schedules?: Array<{ attendees?: Array<{ price?: number }> }>;
            };
            const cartService = service as unknown as CartTrackingService;
            const isExternalService = ['CONCIERGERIE', 'PARKING'].includes(cartService.type ?? '');
            return {
              upsell: 'yes',
              transfer: cartService.type === 'TRANSFER' ? 'yes without transport' : 'no',
              upsellCategory: isExternalService
                ? cartService.type?.toLowerCase()
                : getActivityTypeName(cartService.type ?? ''),
              upsellTransaction: 1,
              stayIndex: cart.stayIndex,
              pickYourRoom: 'no',
              upsellName: isExternalService
                ? cartService.serviceInfo?.subtype?.toLowerCase()
                : cartService.productId,
              ...(cartService.schedules ?? []).reduce(
                (ac, { attendees = [] }) => {
                  ac.upsellRevenue += attendees.reduce(
                    (intermediatePrice, { price = 0 }) => intermediatePrice + price,
                    0,
                  );

                  if (['TRANSFER', 'INSURANCE'].includes(cartService.type ?? '')) {
                    ac.upsellQuantity = 1;
                  } else {
                    ac.upsellQuantity += attendees.length;
                  }

                  return ac;
                },
                {
                  upsellRevenue: 0,
                  upsellQuantity: 0,
                },
              ),
            };
          });
        trackingData = values(groupBy(trackingData, 'upsellName')).map((entries) =>
          entries.reduce<Record<string, unknown>>(
            (acc, cur) =>
              isEmpty(acc)
                ? cur
                : {
                    ...acc,
                    upsellQuantity: (acc.upsellQuantity as number) + (cur.upsellQuantity as number),
                    upsellRevenue: (acc.upsellRevenue as number) + (cur.upsellRevenue as number),
                  },
            {},
          ),
        ) as Array<Record<string, unknown>>;

        if (pickYourRoomUpsell) {
          trackingData.push({
            upsell: 'yes',
            transfer: 'no',
            pickYourRoom: 'yes',
            upsellCategory: 'pickYourRoom',
            upsellTransaction: 1,
            upsellName: 'pickYourRoom',
            upsellRevenue: pickYourRoomUpsell.amount,
            upsellQuantity: 1,
          });
        }

        return [bookingCart.bookingId, trackingData];
      });
    });
};

export { formatCartBookings, formatReservedServicesByAttendee, formatCartTrackingData };
